import React, { useEffect, useState } from 'react';
import { Search, Plus, Filter, Download, Users, Eye, Edit2, Trash2, RefreshCw, Loader2, XCircle, Phone, Mail } from 'lucide-react';
import { ContactModal } from '../components/ContactModal';
import { contactsApi, CrmContact } from '../services/contactsApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
export function Contacts() {
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const [contacts, setContacts] = useState<CrmContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  // Modal states
  const [showContactModal, setShowContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState<CrmContact | null>(null);
  const [viewingContact, setViewingContact] = useState<CrmContact | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const fetchContacts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await contactsApi.list();
      setContacts(response.data || []);
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
      const msg = err instanceof Error ? err.message : 'Failed to load contacts.';
      setError(msg);
      toast.error('Failed to load contacts', {
        description: msg
      });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchContacts();
  }, []);
  const handleAddContact = () => {
    setEditingContact(null);
    setShowContactModal(true);
  };
  const handleEditContact = (contact: CrmContact) => {
    setEditingContact(contact);
    setShowContactModal(true);
  };
  const handleViewContact = async (contact: CrmContact) => {
    setViewingContact(contact);
    try {
      const fullDetails = await contactsApi.get(contact.id);
      if (fullDetails) {
        setViewingContact(fullDetails);
      }
    } catch (err) {
      console.error('Failed to fetch contact details:', err);
      toast.warning('Could not load full details', {
        description: 'Showing partial data.'
      });
    }
  };
  const handleDeleteContact = async (id: string) => {
    setIsDeleting(true);
    try {
      await contactsApi.delete(id);
      setContacts(prev => prev.filter(c => c.id !== id));
      setShowDeleteConfirm(null);
      toast.success('Contact deleted', {
        description: 'The contact has been removed from Zoho CRM.'
      });
    } catch (err) {
      console.error('Failed to delete contact:', err);
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to delete contact', {
        description: msg
      });
    } finally {
      setIsDeleting(false);
    }
  };
  const handleModalSuccess = () => {
    setShowContactModal(false);
    fetchContacts();
  };
  // Client-side filtering for now (API search would be better for large datasets)
  const filteredContacts = contacts.filter(contact => {
    // Team filter
    if (contact.Team && contact.Team !== currentWorkspace.name) return false;
    // Region filter
    if (currentSubRegion) {
      if (contact.Sub_Region && contact.Sub_Region !== currentSubRegion) return false;
    }
    // Search filter
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    const fullName = contact.Full_Name || `${contact.First_Name || ''} ${contact.Last_Name || ''}`;
    return contact.Email && contact.Email.toLowerCase().includes(searchLower) || fullName.toLowerCase().includes(searchLower);
  });
  return <div className="space-y-6" data-id="element-2141">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-2142">
        <div className="min-w-0" data-id="element-2143">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-2144">Contacts</h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-2145">
            Manage your CRM contacts directly from here.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0" data-id="element-2146">
          <button onClick={fetchContacts} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 whitespace-nowrap" title="Refresh List" data-id="element-2147">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-2148" />
          </button>
          <button className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 whitespace-nowrap" data-id="element-2149">
            <Download className="h-4 w-4 flex-shrink-0" data-id="element-2150" />
            <span data-id="element-2151">Export</span>
          </button>
          <button onClick={handleAddContact} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap" data-id="element-2152">
            <Plus className="h-4 w-4 flex-shrink-0" data-id="element-2153" />
            <span data-id="element-2154">Add Contact</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 bg-white border border-gray-200 rounded-lg p-4" data-id="element-2155">
        <div className="relative flex-1 min-w-0" data-id="element-2156">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-2157" />
          <input type="text" placeholder="Search by email or name..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 pr-4 py-2 w-full border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" data-id="element-2158" />
        </div>
        <div className="flex items-center gap-2 flex-shrink-0" data-id="element-2159">
          <Filter className="h-4 w-4 text-gray-400 hidden sm:block flex-shrink-0" data-id="element-2160" />
          <span className="text-sm text-gray-500" data-id="element-2161">
            {filteredContacts.length} records
          </span>
        </div>
      </div>

      {/* Error State */}
      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm" data-id="element-2162">
          {error}
        </div>}

      {/* Contacts Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-2163">
        {loading && contacts.length === 0 ? <div className="p-12 text-center" data-id="element-2164">
            <Loader2 className="mx-auto h-8 w-8 text-blue-500 animate-spin" data-id="element-2165" />
            <p className="mt-2 text-sm text-gray-500" data-id="element-2166">Loading contacts...</p>
          </div> : filteredContacts.length === 0 ? <div className="p-12 text-center" data-id="element-2167">
            <Users className="mx-auto h-12 w-12 text-gray-400" data-id="element-2168" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-2169">
              No contacts found
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-2170">
              {searchQuery ? 'Try adjusting your search.' : 'Add your first contact to get started.'}
            </p>
            {!searchQuery && <button onClick={handleAddContact} className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-2171">
                <Plus className="h-4 w-4" data-id="element-2172" />
                Add First Contact
              </button>}
          </div> : <div className="overflow-x-auto" data-id="element-2173">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-2174">
              <thead className="bg-gray-50" data-id="element-2175">
                <tr data-id="element-2176">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-2177">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-2178">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-2179">
                    Phone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-2180">
                    Tags
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-2181">
                    Mailing Lists
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-2182">
                    Categories
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-2183">
                    Created Time
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-2184">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-2185">
                {filteredContacts.map(contact => <tr key={contact.id} className="hover:bg-gray-50 group" data-id="element-2186">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2187">
                      <div className="flex items-center gap-3" data-id="element-2188">
                        <div className="h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center text-sm font-medium text-gray-600 flex-shrink-0" data-id="element-2189">
                          {(contact.First_Name?.[0] || contact.Last_Name?.[0] || '?').toUpperCase()}
                        </div>
                        <div className="min-w-0" data-id="element-2190">
                          <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]" data-id="element-2191">
                            {contact.Full_Name || `${contact.First_Name || ''} ${contact.Last_Name || ''}`}
                          </div>
                          {contact.Title && <div className="text-xs text-gray-500 truncate" data-id="element-2192">
                              {contact.Title}
                            </div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2193">
                      {contact.Email ? <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-2194">
                          <Mail className="h-3 w-3 text-gray-400" data-id="element-2195" />
                          {contact.Email}
                        </div> : <span className="text-xs text-gray-400" data-id="element-2196">—</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2197">
                      {contact.Phone || contact.Mobile ? <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-2198">
                          <Phone className="h-3 w-3 text-gray-400" data-id="element-2199" />
                          {contact.Phone || contact.Mobile}
                        </div> : <span className="text-xs text-gray-400" data-id="element-2200">—</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2201">
                      {contact.Tags ? <div className="flex flex-wrap gap-1 max-w-[200px]" data-id="element-2202">
                          {contact.Tags.split(',').slice(0, 2).map((tag, i) => <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-2203">
                                {tag.trim()}
                              </span>)}
                          {contact.Tags.split(',').length > 2 && <span className="text-xs text-gray-500" data-id="element-2204">
                              +{contact.Tags.split(',').length - 2}
                            </span>}
                        </div> : <span className="text-xs text-gray-400" data-id="element-2205">—</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2206">
                      {contact.Mailing_List ? <div className="flex flex-wrap gap-1 max-w-[200px]" data-id="element-2207">
                          {contact.Mailing_List.split(',').slice(0, 2).map((list, i) => <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700" data-id="element-2208">
                                {list.trim()}
                              </span>)}
                          {contact.Mailing_List.split(',').length > 2 && <span className="text-xs text-gray-500" data-id="element-2209">
                              +{contact.Mailing_List.split(',').length - 2}
                            </span>}
                        </div> : <span className="text-xs text-gray-400" data-id="element-2210">—</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2211">
                      {contact.Categories ? <div className="flex flex-wrap gap-1 max-w-[200px]" data-id="element-2212">
                          {contact.Categories.split(',').slice(0, 2).map((cat, i) => <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700" data-id="element-2213">
                                {cat.trim()}
                              </span>)}
                          {contact.Categories.split(',').length > 2 && <span className="text-xs text-gray-500" data-id="element-2214">
                              +{contact.Categories.split(',').length - 2}
                            </span>}
                        </div> : <span className="text-xs text-gray-400" data-id="element-2215">—</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2216">
                      {contact.Created_Time ? new Date(contact.Created_Time).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right" data-id="element-2217">
                      <div className="flex items-center justify-end gap-1" data-id="element-2218">
                        <button onClick={() => handleViewContact(contact)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded flex-shrink-0" title="View Details" data-id="element-2219">
                          <Eye className="h-4 w-4" data-id="element-2220" />
                        </button>
                        <button onClick={() => handleEditContact(contact)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded flex-shrink-0" title="Edit Contact" data-id="element-2221">
                          <Edit2 className="h-4 w-4" data-id="element-2222" />
                        </button>
                        <button onClick={() => setShowDeleteConfirm(contact.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded flex-shrink-0" title="Delete Contact" data-id="element-2223">
                          <Trash2 className="h-4 w-4" data-id="element-2224" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>

      {/* Contact Modal (Add/Edit) */}
      <ContactModal isOpen={showContactModal} onClose={() => {
      setShowContactModal(false);
      setEditingContact(null);
    }} contact={editingContact} onSuccess={handleModalSuccess} data-id="element-2225" />

      {/* View Contact Detail Modal */}
      {viewingContact && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-2226">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-2227">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setViewingContact(null)} data-id="element-2228" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg" data-id="element-2229">
              <div className="px-6 py-4 border-b border-gray-200" data-id="element-2230">
                <div className="flex items-center justify-between" data-id="element-2231">
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-2232">
                    Contact Details
                  </h3>
                  <button onClick={() => setViewingContact(null)} className="text-gray-400 hover:text-gray-500" data-id="element-2233">
                    <XCircle className="h-5 w-5" data-id="element-2234" />
                  </button>
                </div>
              </div>
              <div className="px-6 py-4 space-y-4" data-id="element-2235">
                <div className="flex items-center gap-4" data-id="element-2236">
                  <div className="h-14 w-14 rounded-full bg-gray-100 flex items-center justify-center text-lg font-medium text-gray-600" data-id="element-2237">
                    {(viewingContact.First_Name?.[0] || viewingContact.Last_Name?.[0] || '?').toUpperCase()}
                  </div>
                  <div data-id="element-2238">
                    <h4 className="text-lg font-medium text-gray-900" data-id="element-2239">
                      {viewingContact.Full_Name || `${viewingContact.First_Name || ''} ${viewingContact.Last_Name || ''}`}
                    </h4>
                    <p className="text-sm text-gray-500" data-id="element-2240">
                      {viewingContact.Title}{' '}
                      {viewingContact.Title && viewingContact.Department ? '•' : ''}{' '}
                      {viewingContact.Department}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100" data-id="element-2241">
                  <div data-id="element-2242">
                    <p className="text-xs font-medium text-gray-500 uppercase" data-id="element-2243">
                      Email
                    </p>
                    <p className="mt-1 text-sm text-gray-900 break-all" data-id="element-2244">
                      {viewingContact.Email || '—'}
                    </p>
                  </div>
                  <div data-id="element-2245">
                    <p className="text-xs font-medium text-gray-500 uppercase" data-id="element-2246">
                      Phone
                    </p>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-2247">
                      {viewingContact.Phone || '—'}
                    </p>
                  </div>
                  <div data-id="element-2248">
                    <p className="text-xs font-medium text-gray-500 uppercase" data-id="element-2249">
                      Mobile
                    </p>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-2250">
                      {viewingContact.Mobile || '—'}
                    </p>
                  </div>
                  <div data-id="element-2251">
                    <p className="text-xs font-medium text-gray-500 uppercase" data-id="element-2252">
                      Owner
                    </p>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-2253">
                      {viewingContact.Owner?.name || '—'}
                    </p>
                  </div>
                  <div data-id="element-2254">
                    <p className="text-xs font-medium text-gray-500 uppercase" data-id="element-2255">
                      Tags
                    </p>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-2256">
                      {viewingContact.Tags || '—'}
                    </p>
                  </div>
                  <div data-id="element-2257">
                    <p className="text-xs font-medium text-gray-500 uppercase" data-id="element-2258">
                      Mailing Lists
                    </p>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-2259">
                      {viewingContact.Mailing_List || '—'}
                    </p>
                  </div>
                  <div className="col-span-2" data-id="element-2260">
                    <p className="text-xs font-medium text-gray-500 uppercase" data-id="element-2261">
                      Categories
                    </p>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-2262">
                      {viewingContact.Categories || '—'}
                    </p>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-100 text-xs text-gray-400" data-id="element-2263">
                  <p data-id="element-2264">
                    Created:{' '}
                    {viewingContact.Created_Time ? new Date(viewingContact.Created_Time).toLocaleString() : '—'}
                  </p>
                  <p data-id="element-2265">
                    Modified:{' '}
                    {viewingContact.Modified_Time ? new Date(viewingContact.Modified_Time).toLocaleString() : '—'}
                  </p>
                  <p className="mt-1 font-mono" data-id="element-2266">ID: {viewingContact.id}</p>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3" data-id="element-2267">
                <button onClick={() => setViewingContact(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2268">
                  Close
                </button>
                <button onClick={() => {
              handleEditContact(viewingContact);
              setViewingContact(null);
            }} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-2269">
                  Edit Contact
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-2270">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-2271">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowDeleteConfirm(null)} data-id="element-2272" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-2273">
              <div className="flex items-center gap-4" data-id="element-2274">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-2275">
                  <Trash2 className="h-6 w-6 text-red-600" data-id="element-2276" />
                </div>
                <div data-id="element-2277">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-2278">
                    Delete Contact
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-2279">
                    Are you sure you want to delete this contact from Zoho CRM?
                    This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6" data-id="element-2280">
                <button onClick={() => setShowDeleteConfirm(null)} disabled={isDeleting} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2281">
                  Cancel
                </button>
                <button onClick={() => handleDeleteContact(showDeleteConfirm)} disabled={isDeleting} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2" data-id="element-2282">
                  {isDeleting && <Loader2 className="h-4 w-4 animate-spin" data-id="element-2283" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}